import React, { useEffect, useRef, useState } from 'react'
import { globalNotifications } from 'src/notifications'

interface IPotSizeData {
  currentValue: string
  previousValue: string
  changed: boolean
}

const LottoMarquee: React.FC<{potSize?: number}> = ({potSize}) => {
  const prevPotSize = useRef<number>(0)
  const [newPotSize, setNewPotSize] = useState(0)
  const potSizeData = useRef<IPotSizeData[]>(
    new Array(7).fill({ currentValue: '0', previousValue: '0', changed: false })
  )

  useEffect(() => {
    const handler = async () => {
      setNewPotSize(Math.floor(potSize ?? 0))
    }
    if (potSize !== prevPotSize.current) {
      handler()
    }
    globalNotifications.on('potSizeChanged', handler)
    return () => globalNotifications.off('potSizeChanged', handler)
  }, [prevPotSize, newPotSize, setNewPotSize, potSize])

  useEffect(() => {
    const padAmount = Math.max(7, newPotSize.toString().length)
    const previousValueFormatted = (prevPotSize.current ?? 0)
      .toString()
      .padStart(padAmount, '0')
    const currentValueFormatted = (newPotSize ?? 0)
      .toString()
      .padStart(padAmount, '0')
    potSizeData.current = currentValueFormatted.split('').map((char, index) => {
      const reverseIndex = -index + currentValueFormatted.length - 1
      const diffLength = (newPotSize - (prevPotSize.current ?? 0)).toString()
        .length
      const prevChar = previousValueFormatted?.[index] ?? '0'
      return {
        currentValue: char,
        previousValue: prevChar,
        changed: diffLength > reverseIndex || prevChar !== char,
      }
    })

    prevPotSize.current = newPotSize
  }, [newPotSize])

  useEffect(() => {
    const resetNumbers = setTimeout(() => {
      potSizeData.current.forEach((data) => {
        data.changed = false
      }, 4000)
    })

    return () => clearTimeout(resetNumbers)
  }, [potSizeData])

  return (
    <div className="relative z-1">
      <div className="absolute left-0 bottom-0 z-0 h-[74%] w-full p-[9%]">
        <div className="w-full h-full flex justify-stretch gap-1 sm:gap-1.5">
          {potSizeData.current.map((data: IPotSizeData, index) => (
            <div
              key={index}
              className={`marquee-number ${(index - potSizeData.current.length) % 3 === 0 ? 'ml-1' : ''} ${
                potSizeData.current.length <= 7 ? 'text-4xl sm:text-[2.75rem]' : 'text-3xl sm:text-4xl'
              }`}
            >
              <p
                style={{
                  animationDelay: `${(-index + potSizeData.current.length) / 4 + 1.1}s`,
                }}
                className={`current-number ${data.changed ? 'anim-3' : ''}`}
              >
                {data.currentValue}
              </p>
              <p
                style={{
                  animationDelay: `${(-index + potSizeData.current.length) / 4 + 0.25}s`,
                }}
                className={`question-mark ${data.changed ? 'anim-2' : ''}`}
              >
                ?
              </p>
              <p
                style={{
                  animationDelay: `${(-index + potSizeData.current.length) / 4}s`,
                }}
                className={`prev-number ${data.changed ? 'anim-1' : ''}`}
              >
                {data.previousValue}
              </p>
            </div>
          ))}
        </div>
      </div>

      <svg
        version="1.1"
        id="Layer_1"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        x="0px"
        y="0px"
        viewBox="0 0 307 149"
        className='w-[1000px] max-w-full'
      >
        <g>
          <path
            className="st0"
            d="M16.1,40.2h274.7c7.6,0,13.8,6.2,13.8,13.8v79.2c0,7.6-6.2,13.8-13.8,13.8H16.1c-7.6,0-13.8-6.2-13.8-13.8V54
            C2.3,46.4,8.5,40.2,16.1,40.2z"
          />
          <g>
            <path
              shapeRendering="crispEdges"
              className="st1"
              d="M17.8,45.4h271.3c5.7,0,10.3,4.6,10.3,10.3v75.7c0,5.7-4.6,10.3-10.3,10.3H17.8
            c-5.7,0-10.3-4.6-10.3-10.3V55.7C7.5,50,12.1,45.4,17.8,45.4z"
            />

            <linearGradient
              id="SVGID_1_"
              gradientUnits="userSpaceOnUse"
              x1="19.8712"
              y1="127.0589"
              x2="287.8414"
              y2="-14.6838"
              gradientTransform="matrix(1 0 0 -1 0 150)"
            >
              <stop offset="0" style={{ stopColor: '#008CFF' }} />
              <stop offset="0.595" style={{ stopColor: '#DBFF00' }} />
              <stop offset="1" style={{ stopColor: '#B300FF' }} />
            </linearGradient>
            <path
              shapeRendering="crispEdges"
              className="st2"
              d="M17.8,45.4h271.3c5.7,0,10.3,4.6,10.3,10.3v75.7c0,5.7-4.6,10.3-10.3,10.3H17.8
            c-5.7,0-10.3-4.6-10.3-10.3V55.7C7.5,50,12.1,45.4,17.8,45.4z"
            />
            <path
              shapeRendering="crispEdges"
              className="st3"
              d="M17.8,45.8h271.3c5.5,0,9.9,4.4,9.9,9.9v75.7c0,5.5-4.4,9.9-9.9,9.9H17.8
            c-5.5,0-9.9-4.4-9.9-9.9V55.7C7.9,50.3,12.3,45.8,17.8,45.8z"
            />
            <g>
              <linearGradient
                id="SVGID_00000060006209031603927040000001951229543799559102_"
                gradientUnits="userSpaceOnUse"
                x1="136.5582"
                y1="139.9003"
                x2="170.9852"
                y2="105.4725"
                gradientTransform="matrix(1 0 0 -1 0 150)"
              >
                <stop offset="0" style={{ stopColor: '#DCF6FF' }} />
                <stop offset="0.485" style={{ stopColor: '#557399' }} />
                <stop offset="0.78" style={{ stopColor: '#557399' }} />
                <stop offset="1" style={{ stopColor: '#97C5FF' }} />
              </linearGradient>
              <path
                style={{
                  fill: '#121212',
                  stroke:
                    'url(#SVGID_00000060006209031603927040000001951229543799559102_)',
                  strokeWidth: '2.5821',
                }}
                d="
                M172.3,27.3c0-10.2-8.3-18.5-18.5-18.5s-18.5,8.3-18.5,18.5s8.3,18.5,18.5,18.5S172.3,37.5,172.3,27.3z"
              />
              <path
                className="st5"
                d="M149.8,28.1l1.8,1V35l-5.1-3v-7.9l-1.7-1V33l8.6,5v-9.8l-3.6-2.1V28.1z"
              />
              <path
                className="st5"
                d="M154.5,28.1v4.1l1.7-1v-2.1l5.1-3v5.9l-6.9,4v2l8.6-5v-9.9L154.5,28.1z"
              />
              <path
                className="st5"
                d="M155.7,24.1l-1.8,1l-5.1-3l5.1-3l6.9,4l1.7-1l-8.6-5l-8.6,5l8.6,5l3.6-2.1L155.7,24.1z"
              />
            </g>
            <g>
              <path
                className="st0"
                d="M28.1,59.2h250.6c3.8,0,6.9,3.1,6.9,6.9v55.1c0,3.8-3.1,6.9-6.9,6.9H28.1c-3.8,0-6.9-3.1-6.9-6.9V66
                C21.2,62.2,24.3,59.2,28.1,59.2z"
              />
              <path
                className="st3"
                d="M28.1,59.6h250.6c3.6,0,6.5,2.9,6.5,6.5v55.1c0,3.6-2.9,6.5-6.5,6.5H28.1c-3.6,0-6.5-2.9-6.5-6.5V66
                C21.7,62.5,24.5,59.6,28.1,59.6z"
              />
            </g>
            <linearGradient id="light-off">
              <stop offset="0" style={{ stopColor: '#98BFFF' }} />
              <stop offset="0.4867" style={{ stopColor: '#4375C5' }} />
              <stop offset="1" style={{ stopColor: '#98BFFF' }} />
            </linearGradient>
            <linearGradient id="light-on">
              <stop offset="0" style={{ stopColor: '#F0FF98' }} />
              <stop offset="0.4867" style={{ stopColor: '#A2B534' }} />
              <stop offset="1" style={{ stopColor: '#F0FF98' }} />
            </linearGradient>
            <g id="bubble-24" className="bubble-even">
              <circle cx="16.9" cy="131.5" r="3.4" />
            </g>
            <g id="bubble-22" className="bubble-even">
              <circle cx="71.1" cy="134.9" r="3.4" />
            </g>
            <g id="bubble-20" className="bubble-even">
              <circle cx="126.2" cy="134.9" r="3.4" />
            </g>
            <g id="bubble-26" className="bubble-even">
              <circle cx="14.8" cy="79.8" r="3.4" />
            </g>
            <g id="bubble-4" className="bubble-even">
              <circle cx="98.7" cy="52.3" r="3.4" />
            </g>
            <g id="bubble-2" className="bubble-even">
              <circle cx="43.6" cy="52.3" r="3.4" />
            </g>
            <g id="bubble-23" className="bubble-odd">
              <circle cx="43.6" cy="134.9" r="3.4" />
            </g>
            <g id="bubble-21" className="bubble-odd">
              <circle cx="98.7" cy="134.9" r="3.4" />
            </g>
            <g id="bubble-1" className="bubble-odd">
              <circle cx="17.4" cy="55.7" r="3.4" />
            </g>
            <g id="bubble-5" className="bubble-odd">
              <circle cx="126.2" cy="52.3" r="3.4" />
            </g>
            <g id="bubble-3" className="bubble-odd">
              <circle cx="71.1" cy="52.3" r="3.4" />
            </g>
            <g id="bubble-25" className="bubble-odd">
              <circle cx="14.8" cy="106.5" r="3.4" />
            </g>
            <g id="bubble-14" className="bubble-even">
              <circle cx="289.8" cy="131.5" r="3.4" />
            </g>
            <g id="bubble-16" className="bubble-even">
              <circle cx="235.5" cy="134.9" r="3.4" />
            </g>
            <g id="bubble-18" className="bubble-even">
              <circle cx="181.7" cy="134.9" r="3.4" />
            </g>
            <g id="bubble-12" className="bubble-even">
              <circle cx="291.9" cy="79.8" r="3.4" />
            </g>
            <g id="bubble-8" className="bubble-even">
              <circle cx="208" cy="52.3" r="3.4" />
            </g>
            <g id="bubble-10" className="bubble-even">
              <circle cx="263.1" cy="52.3" r="3.4" />
            </g>
            <g id="bubble-15" className="bubble-odd">
              <circle cx="263.1" cy="134.9" r="3.4" />
            </g>
            <g id="bubble-17" className="bubble-odd">
              <circle cx="208" cy="134.9" r="3.4" />
            </g>
            <g id="bubble-11" className="bubble-odd">
              <circle cx="289.3" cy="55.7" r="3.4" />
            </g>
            <g id="bubble-7" className="bubble-odd">
              <circle cx="180.5" cy="52.3" r="3.4" />
            </g>
            <g id="bubble-9" className="bubble-odd">
              <circle cx="235.5" cy="52.3" r="3.4" />
            </g>
            <g id="bubble-13" className="bubble-odd">
              <circle cx="291.9" cy="106.5" r="3.4" />
            </g>
            <g id="bubble-6" className="bubble-even">
              <circle cx="153.8" cy="52.3" r="3.4" />
            </g>
            <g id="bubble-19" className="bubble-odd">
              <circle cx="153.8" cy="134.9" r="3.4" />
            </g>
          </g>
        </g>
      </svg>
    </div>
  )
}

export default LottoMarquee
